import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, ImageDown, Loader2, Check } from "lucide-react";
import { api, formatData, setMeta, shareCard } from "@/lib/museo";
import { track } from "@/lib/analytics";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";

export default function Confirm() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading"); // loading | success | error
  const [promise, setPromise] = useState(null);
  const [copied, setCopied] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    setMeta({ title: "Il tuo messaggio è entrato nel museo — Il Museo delle Promesse" });
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 12) {
        setState("error");
        return;
      }
      attempts.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid" && r.data.promise) {
          setPromise(r.data.promise);
          setState("success");
          track("purchase", {
            currency: "EUR",
            value: 1.99,
            transaction_id: sessionId,
          });
          track("message_published", { numero: r.data.promise.numero });
          return;
        }
        if (r.data.status === "expired") {
          setState("error");
          return;
        }
      } catch {
        /* retry */
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const publicUrl = promise
    ? `${window.location.origin}/promessa/${String(promise.numero).padStart(6, "0")}`
    : "";

  const handleCopy = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("Link copiato");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    await shareCard({ numero: promise.numero, testo: promise.testo, firma: promise.firma, toast });
  };

  if (state === "loading") {
    return (
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <Loader2 className="w-7 h-7 text-museo-accent animate-spin" />
        <p className="font-serif text-2xl text-museo-text">Stiamo registrando il tuo messaggio…</p>
        <p className="text-sm text-museo-muted font-light">Verifica del pagamento in corso.</p>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-serif text-3xl text-museo-text">Non siamo riusciti a confermare il pagamento.</p>
        <p className="text-sm text-museo-muted font-light max-w-md">
          Se il pagamento è andato a buon fine, la promessa apparirà a breve nel museo.
        </p>
        <Link to="/" data-testid="confirm-back-home" className="text-museo-accent uppercase text-sm tracking-[0.2em] hover:text-museo-text transition-colors">
          Torna al museo
        </Link>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <div className="flex-1 max-w-3xl mx-auto px-6 md:px-12 pt-20 md:pt-28 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="flex flex-col items-center text-center gap-8"
        >
          <h1 className="font-serif text-4xl md:text-5xl text-museo-text leading-tight">
            Il tuo messaggio è entrato nel museo.
          </h1>

          <div className="w-full bg-museo-surface border border-museo-border p-8 md:p-12 rounded-none md:rounded-sm flex flex-col items-center gap-6 mt-4">
            <span className="font-mono text-sm tracking-[0.3em] text-museo-accent" data-testid="confirm-numero">
              Messaggio {promise.numero_formatted}
            </span>
            <p className="font-serif text-2xl md:text-3xl leading-relaxed text-museo-text" data-testid="confirm-testo">
              “{promise.testo}”
            </p>
            <span className="font-serif italic text-base text-museo-muted" data-testid="confirm-firma">
              {promise.firma ? `— ${promise.firma}` : "— Anonimo"}
            </span>
            <span className="text-sm text-museo-muted font-light">{formatData(promise.data)}</span>
          </div>

          <p className="text-sm text-museo-muted font-light max-w-lg">
            Questo messaggio ora fa parte del Museo delle Promesse.
          </p>

          <div className="w-full flex items-center gap-3 bg-museo-bg border border-museo-border p-3 rounded-none md:rounded-sm">
            <span data-testid="confirm-public-link" className="flex-1 text-left text-xs md:text-sm text-museo-muted font-mono truncate px-2">
              {publicUrl}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
            <button
              data-testid="copy-link-button"
              onClick={handleCopy}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent border border-museo-border text-museo-text px-8 py-4 text-sm tracking-[0.1em] uppercase hover:border-museo-accent transition-all duration-500 rounded-none md:rounded-sm"
            >
              {copied ? <Check className="w-4 h-4 text-museo-accent" /> : <Copy className="w-4 h-4" />} Copia link
            </button>
            <button
              data-testid="confirm-share-button"
              onClick={handleShare}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-museo-accent text-museo-bg px-8 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-museo-text transition-all duration-500 rounded-none md:rounded-sm"
            >
              <ImageDown className="w-4 h-4" /> Condividi messaggio
            </button>
          </div>

          <Link
            to={`/promessa/${String(promise.numero).padStart(6, "0")}`}
            data-testid="confirm-view-page"
            className="text-museo-muted text-sm uppercase tracking-[0.15em] hover:text-museo-accent transition-colors mt-2"
          >
            Apri la pagina pubblica
          </Link>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
